import { useState, useEffect } from 'react'
import { X, RefreshCw, Truck, Package } from 'lucide-react'
import api from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

const CARRIERS = [
  { code: 'mrw', name: 'MRW' },
  { code: 'gls', name: 'GLS' },
  { code: 'ctt', name: 'CTT Express' }
]

interface Props {
  isOpen: boolean
  onClose: () => void
  returnData: {
    id: number
    code: string
    customer_name: string
    customer_email: string
  }
  onCompleted: () => void
}

export default function ReplacementReturnModal({ isOpen, onClose, returnData, onCompleted }: Props) {
  const [replacementCarrier, setReplacementCarrier] = useState('')
  const [replacementTracking, setReplacementTracking] = useState('')
  const [replacementNotes, setReplacementNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const toast = useToast()

  useEffect(() => {
    if (isOpen) {
      setReplacementCarrier('')
      setReplacementTracking('')
      setReplacementNotes('')
    }
  }, [isOpen, returnData.id])

  const handleSubmit = async () => {
    setLoading(true)
    try {
      await api.post(`/returns/admin/${returnData.id}/replacement`, {
        replacement_carrier: replacementCarrier || undefined,
        replacement_tracking: replacementTracking.trim() || undefined,
        replacement_notes: replacementNotes.trim() || undefined
      })

      toast.success('Cambio por fallo procesado', `Envío de sustitución registrado para ${returnData.code}`)
      onCompleted()
      onClose()
    } catch (error: any) {
      toast.error('Error', error.message || 'Error al procesar cambio por fallo')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <RefreshCw className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Cambio por fallo</h2>
              <p className="text-sm text-slate-500">{returnData.code} — {returnData.customer_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4 border border-orange-200 dark:border-orange-800">
            <div className="flex items-start gap-3">
              <Package className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-orange-800 dark:text-orange-400">Envío de sustitución</p>
                <p className="text-orange-700 dark:text-orange-300 mt-1">
                  Se enviará una unidad de reemplazo al cliente sin realizar ningún reembolso económico.
                  Se notificará al cliente por email.
                </p>
              </div>
            </div>
          </div>

          {/* Transportista */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              <div className="flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Transportista
              </div>
            </label>
            <select
              value={replacementCarrier}
              onChange={(e) => setReplacementCarrier(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                       bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            >
              <option value="">Seleccionar transportista...</option>
              {CARRIERS.map(c => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Tracking del envío */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Número de seguimiento (opcional)
            </label>
            <input
              type="text"
              value={replacementTracking}
              onChange={(e) => setReplacementTracking(e.target.value)}
              placeholder="Ej: 0123456789"
              className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                       bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder:text-slate-400"
            />
            <p className="text-xs text-slate-500 mt-1">Si ya dispone del tracking del envío, puede indicarlo aquí</p>
          </div>

          {/* Notas */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Notas internas (opcional)
            </label>
            <textarea
              value={replacementNotes}
              onChange={(e) => setReplacementNotes(e.target.value)}
              rows={3}
              placeholder="Ej: Se envía unidad nueva REF-XXXX por defecto de fabricación..."
              className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                       bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder:text-slate-400 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Procesando...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Registrar cambio
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
